/** Cached reference data (accounts, parties, entities) and the pickers built on it. */
import { useMemo } from 'react';
import { useApi } from './api';
import type { Account, BankAccount, Customer, Product, Subsidiary, Vendor } from './types';
import { Select } from '../components/ui';

const LONG = { staleTime: 5 * 60_000 };

export function useAccounts() {
  const q = useApi<{ accounts: Account[]; subtypes: Record<string, string[]> }>('/api/accounts', LONG);
  const byId = useMemo(() => new Map((q.data?.accounts ?? []).map((a) => [a.id, a])), [q.data]);
  return { ...q, accounts: q.data?.accounts ?? [], subtypes: q.data?.subtypes ?? {}, byId };
}

export function useSubsidiaries() {
  const q = useApi<Subsidiary[]>('/api/subsidiaries', LONG);
  const byId = useMemo(() => new Map((q.data ?? []).map((s) => [s.id, s])), [q.data]);
  return { ...q, list: q.data ?? [], byId };
}

/** `list` has everything, for names in history; `active` leaves out archived records, for pickers of new work. */
function useActive<T extends { archivedAt: string | null }>(data: T[] | undefined) {
  return useMemo(() => (data ?? []).filter((x) => !x.archivedAt), [data]);
}

export function useCustomers() {
  const q = useApi<Customer[]>('/api/customers', LONG);
  const byId = useMemo(() => new Map((q.data ?? []).map((c) => [c.id, c])), [q.data]);
  return { ...q, list: q.data ?? [], active: useActive(q.data), byId };
}

export function useVendors() {
  const q = useApi<Vendor[]>('/api/vendors', LONG);
  const byId = useMemo(() => new Map((q.data ?? []).map((v) => [v.id, v])), [q.data]);
  return { ...q, list: q.data ?? [], active: useActive(q.data), byId };
}

export function useProducts() {
  const q = useApi<Product[]>('/api/products', LONG);
  const byId = useMemo(() => new Map((q.data ?? []).map((p) => [p.id, p])), [q.data]);
  return { ...q, list: q.data ?? [], active: useActive(q.data), byId };
}

export function useBankAccounts() {
  const q = useApi<BankAccount[]>('/api/bank-accounts');
  const byId = useMemo(() => new Map((q.data ?? []).map((b) => [b.id, b])), [q.data]);
  return { ...q, list: q.data ?? [], active: useActive(q.data), byId };
}

const TYPE_ORDER = ['ASSET', 'LIABILITY', 'EQUITY', 'INCOME', 'EXPENSE'] as const;
const TYPE_LABEL: Record<string, string> = { ASSET: 'Assets', LIABILITY: 'Liabilities', EQUITY: 'Equity', INCOME: 'Income', EXPENSE: 'Expenses' };

export function AccountSelect({ value, onChange, types, placeholder = 'Select account…', className, invalid }: { value: string; onChange: (id: string) => void; types?: string[]; placeholder?: string; className?: string; invalid?: boolean }) {
  const { accounts } = useAccounts();
  const groups = TYPE_ORDER.filter((t) => !types || types.includes(t)).map((t) => ({ t, list: accounts.filter((a) => a.type === t && a.isActive) }));
  return (
    <Select value={value} onChange={(e) => onChange(e.target.value)} className={className} aria-invalid={invalid}>
      <option value="">{placeholder}</option>
      {groups.map((g) => (
        <optgroup key={g.t} label={TYPE_LABEL[g.t]}>
          {g.list.map((a) => (
            <option key={a.id} value={a.id}>
              {a.code} · {a.name}
            </option>
          ))}
        </optgroup>
      ))}
    </Select>
  );
}

export function accountLabel(a: Account | undefined) {
  return a ? `${a.code} · ${a.name}` : 'Unknown account';
}
